import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse, AxiosHeaders } from 'axios';
import { getAccessToken } from '../auth/token';

// Use environment variable for API URL with fallback for development.
// Production builds must set REACT_APP_API_URL (e.g. https://api.example.com) or the browser will call localhost.
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Optional debug telemetry (disabled by default).
// Enable by setting:
// - REACT_APP_DEBUG_TELEMETRY=true
// - REACT_APP_DEBUG_TELEMETRY_ENDPOINT=https://... (no default; avoids hardcoded endpoints)
const DEBUG_TELEMETRY_ENDPOINT = process.env.REACT_APP_DEBUG_TELEMETRY_ENDPOINT;
const DEBUG_TELEMETRY_ENABLED =
    process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_DEBUG_TELEMETRY === 'true' &&
    typeof DEBUG_TELEMETRY_ENDPOINT === 'string' &&
    DEBUG_TELEMETRY_ENDPOINT.length > 0;

function sendDebugTelemetry(payload: unknown) {
    if (!DEBUG_TELEMETRY_ENABLED) return;
    fetch(DEBUG_TELEMETRY_ENDPOINT as string, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    }).catch(() => { });
}

const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor for adding auth tokens
client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const urlStr = typeof config.url === 'string' ? config.url : '';
        const isInteresting =
            urlStr.includes('/applications') || urlStr.includes('/statistics') || urlStr.includes('/auth/');

        const token = await getAccessToken();
        if (token) {
            if (!config.headers || !(config.headers instanceof AxiosHeaders)) {
                config.headers = AxiosHeaders.from(config.headers ?? {});
            }
            if (!config.headers.has('Authorization')) {
                config.headers.set('Authorization', `Bearer ${token}`);
            }
        }

        if (DEBUG_TELEMETRY_ENABLED && isInteresting) {
            const base = (config.baseURL as string | undefined) || API_BASE_URL;
            let fullUrl = '';
            try {
                fullUrl = new URL(urlStr, base).toString();
            } catch {
                fullUrl = `${base}${urlStr}`;
            }

            sendDebugTelemetry({
                kind: 'axios_request',
                url: urlStr,
                fullUrl,
                baseURL: base,
                method: config.method,
                withCredentials: Boolean((config as any).withCredentials),
                windowOrigin: typeof window !== 'undefined' ? window.location.origin : null,
                online: typeof navigator !== 'undefined' ? navigator.onLine : null,
                ts: Date.now(),
            });
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Response interceptor for handling errors
client.interceptors.response.use(
    (response: AxiosResponse) => {
        const urlStr = typeof response.config?.url === 'string' ? response.config.url : '';
        const isInteresting = urlStr.includes('/applications') || urlStr.includes('/statistics') || urlStr.includes('/auth/');

        if (DEBUG_TELEMETRY_ENABLED && isInteresting) {
            const base = (response.config?.baseURL as string | undefined) || API_BASE_URL;
            let fullUrl = '';
            try {
                fullUrl = new URL(urlStr, base).toString();
            } catch {
                fullUrl = `${base}${urlStr}`;
            }

            const dataLen = Array.isArray(response.data) ? response.data.length : null;

            sendDebugTelemetry({
                kind: 'axios_response',
                url: urlStr,
                fullUrl,
                status: response.status,
                dataType: typeof response.data,
                dataLen,
                ts: Date.now(),
            });
        }

        return response;
    },
    (error: AxiosError) => {
        // A cancelled request means its page was left or refreshed: still reject, but it is not a failure to log.
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const urlStr = typeof error.config?.url === 'string' ? error.config.url : '';
        const isInteresting = urlStr.includes('/applications') || urlStr.includes('/statistics') || urlStr.includes('/auth/');

        if (DEBUG_TELEMETRY_ENABLED && isInteresting) {
            const base = (error.config?.baseURL as string | undefined) || API_BASE_URL;
            let fullUrl = '';
            try {
                fullUrl = new URL(urlStr, base).toString();
            } catch {
                fullUrl = `${base}${urlStr}`;
            }

            sendDebugTelemetry({
                kind: 'axios_error',
                url: urlStr,
                fullUrl,
                baseURL: base,
                method: error.config?.method ?? null,
                message: error.message,
                code: (error as any).code || null,
                name: (error as any).name || null,
                hasResponse: Boolean(error.response),
                status: error.response?.status ?? null,
                windowOrigin: typeof window !== 'undefined' ? window.location.origin : null,
                online: typeof navigator !== 'undefined' ? navigator.onLine : null,
                ts: Date.now(),
            });
        }

        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error('Response Error:', error.response.data);
        } else if (error.request) {
            // The request was made but no response was received
            console.error('Request Error:', error.request);
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('Error:', error.message);
        }
        return Promise.reject(error);
    }
);

export { client as default };
